import { useEffect, useMemo, useState } from "react";
import type { FestiveEventAdminDto, FestiveEventInput } from "@hrms/types";

import { api, errMsg } from "../../lib/api.js";
import { logError } from "../../lib/logger.js";
import { AsyncButton } from "../../components/ui/async-button.js";
import { Badge } from "../../components/ui/badge.js";
import { Button } from "../../components/ui/button.js";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card.js";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table.js";
import { FestiveEventForm, type OfficeOption } from "./FestiveEventForm.js";

const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Human form of the event's date rule. */
function whenLabel(e: FestiveEventAdminDto): string {
  if (e.recurrence === "ANNUAL") return `${e.day} ${MONTH_SHORT[(e.month ?? 1) - 1]} every year`;
  if (!e.eventDate) return "No date set";
  return new Date(`${e.eventDate}T00:00:00Z`).toLocaleDateString("en-IN", {
    day: "numeric", month: "short", year: "numeric", timeZone: "UTC",
  });
}

/** The whole event as a write body, with the status changed. Used by Pause and Resume. */
function inputFrom(e: FestiveEventAdminDto, isActive: boolean): FestiveEventInput {
  return {
    name: e.name, greeting: e.greeting, message: e.message, icon: e.icon, accent: e.accent,
    recurrence: e.recurrence, month: e.month, day: e.day, eventDate: e.eventDate,
    windowDays: e.windowDays, isActive, allOffices: e.allOffices,
    offices: e.offices.map((o) => o.id), presetKey: null,
  };
}

const WHY = [
  "The built-in festivals (Diwali, Christmas, New Year, Holi, Eid, Independence Day) are already listed. Set their dates each year.",
  "Company events such as a Silver Jubilee, Annual Day or Founders' Day need their own greeting and message. Add them as custom events.",
  "Pick the branches. An event can show to every branch, or only to the branches you choose, and employees in other branches don't see it.",
  "A yearly event repeats on the same day. A one-time event happens once, on the date you set.",
  "Events show from 7 days before by default, and on the day itself. Paused events show nowhere.",
];

/**
 * Admin → Festive Events: the built-in festivals and the company's own events, with the branches
 * that can see each one. Employees see them on the login page (all-branch events only) and on the
 * dashboard (their branch's events).
 */
export default function FestiveEventsPanel() {
  const [events, setEvents] = useState<FestiveEventAdminDto[] | null>(null);
  const [offices, setOffices] = useState<OfficeOption[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<{ event: FestiveEventAdminDto | null } | null>(null);

  const load = async () => {
    try {
      const [ev, of] = await Promise.all([
        api.get<FestiveEventAdminDto[]>("/admin/festive-events"),
        api.get<OfficeOption[]>("/admin/offices"),
      ]);
      setEvents(ev.data);
      setOffices(of.data.map((o) => ({ id: o.id, name: o.name, city: o.city })));
      setError(null);
    } catch (e) {
      logError("FestiveEventsPanel.load", e);
      setError(errMsg(e) ?? "Could not load festive events.");
    }
  };

  useEffect(() => { void load(); }, []);

  const existingPresetKeys = useMemo(
    () => new Set((events ?? []).map((e) => e.presetKey).filter((k): k is string => !!k)),
    [events],
  );

  const togglePaused = async (e: FestiveEventAdminDto) => {
    try {
      await api.patch(`/admin/festive-events/${e.id}`, inputFrom(e, !e.isActive));
      await load();
    } catch (err) {
      setError(errMsg(err) ?? "Could not change the status.");
      throw err;
    }
  };

  const remove = async (e: FestiveEventAdminDto) => {
    try {
      await api.delete(`/admin/festive-events/${e.id}`);
      await load();
    } catch (err) {
      setError(errMsg(err) ?? "Could not delete the event.");
      throw err;
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <CardTitle>Festive events</CardTitle>
              <CardDescription>Festivals and company events shown on the login page and the dashboard.</CardDescription>
            </div>
            <Button onClick={() => setDialog({ event: null })}>Add event</Button>
          </div>
        </CardHeader>
        <CardContent>
          <p className="mb-2 text-sm font-semibold text-surface-700">Why add events here</p>
          <ul className="list-disc space-y-1 pl-5 text-sm text-surface-600">
            {WHY.map((line) => <li key={line}>{line}</li>)}
          </ul>
        </CardContent>
      </Card>

      {error && <p role="alert" className="text-sm text-[var(--color-error-fg)]">{error}</p>}

      <Card>
        <CardContent className="p-0">
          {events === null ? (
            <p className="p-6 text-sm text-surface-500">Loading…</p>
          ) : events.length === 0 ? (
            <p className="p-6 text-sm text-surface-500">No events yet. Add one above, or start from a built-in festival.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Event</TableHead>
                  <TableHead>When</TableHead>
                  <TableHead>Shows</TableHead>
                  <TableHead>Branches</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {events.map((e) => (
                  <TableRow key={e.id}>
                    <TableCell>
                      <p className="font-semibold text-surface-900">{e.name}</p>
                      <p className="text-xs text-surface-500">{e.greeting}{e.presetKey ? " · built-in" : ""}</p>
                    </TableCell>
                    <TableCell>
                      {e.recurrence === "ONE_TIME" && !e.eventDate
                        ? <Badge tone="warning">No date set</Badge>
                        : whenLabel(e)}
                    </TableCell>
                    <TableCell>{e.windowDays} days before</TableCell>
                    <TableCell>
                      {e.allOffices ? "All branches" : e.offices.map((o) => o.name).join(", ")}
                    </TableCell>
                    <TableCell>
                      <Badge tone={e.isActive ? "success" : "neutral"}>{e.isActive ? "Active" : "Paused"}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" size="sm" onClick={() => setDialog({ event: e })}>Edit</Button>
                        <AsyncButton variant="outline" size="sm" animated={false} onClick={() => togglePaused(e)}>
                          {e.isActive ? "Pause" : "Resume"}
                        </AsyncButton>
                        <AsyncButton
                          variant="destructive"
                          size="sm"
                          onClick={() => remove(e)}
                          confirm={{
                            title: `Delete "${e.name}"?`,
                            description: "It disappears for everyone straight away.",
                            actionText: "Delete",
                            tone: "destructive",
                          }}
                        >
                          Delete
                        </AsyncButton>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {dialog && (
        <FestiveEventForm
          key={dialog.event?.id ?? "new"}
          open
          onOpenChange={(open) => { if (!open) setDialog(null); }}
          event={dialog.event}
          existingPresetKeys={existingPresetKeys}
          offices={offices}
          onSaved={() => { setDialog(null); void load(); }}
        />
      )}
    </div>
  );
}
